import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { SkeletonText } from '@parsina/framework';
import { Stethoscope, Wallet, CheckCircle2, Clock } from 'lucide-react';

// The mobile money hero (DESIGN-CONCEPT-MOBILE v2 §4B): the report's answer to
// «امروز چقدر شد؟», designed for a phone rather than shrunk from the desktop KPI
// grid. «سهم شما» is the headline; جمع کل / وصول‌شده / در انتظار وصول read as
// full-width footer rows. It sits below the gradient header (no overlap).
//
// totals: one entry per currency (lib/clinicMoney `reportTotals`), each
//   { currency, grossTotal, doctorShareTotal, collectedDoctorShare, outstanding }
//   in minor units of its own `currency`. One entry — every IRR-only doctor —
//   renders exactly the single hero it always did. Several entries render one
//   hero section per currency, each headed by the currency's name: a combined
//   figure across two currencies does not exist.

// A supporting figure — a full-width «label … value» row rather than a cramped
// 3-up tile, so a large toman amount can never force a horizontal scroll at
// 320px (§6). The value stays whole; the label ellipsises if anything must give.
const Row = ({ Icon, label, value, unit, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <Icon size={14} color={color} aria-hidden style={{ flexShrink: 0 }} />
    <span style={{ flex: 1, minWidth: 0, fontSize: '0.75rem', color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {value}
      <span style={{ fontSize: '0.625rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.125rem' }}>{unit}</span>
    </span>
  </div>
);

// One currency's hero: the share headline and its three supporting rows.
const SummaryGroup = ({ group, loading, heading }) => {
  const { t, formatMoney, moneyUnit } = useTranslation();
  const { currency } = group;
  const share = formatMoney(group.doctorShareTotal, { currency });
  const short = moneyUnit(currency, 'short');

  return (
    <div data-testid={heading ? `accounting-summary-${currency}` : undefined}>
      {heading && (
        <div style={{ fontSize: '0.6875rem', fontWeight: 800, color: C.textSecondary, marginBottom: '0.5rem' }}>{moneyUnit(currency)}</div>
      )}
      {/* Headline — the doctor's own share, the number that matters most on a phone. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <div style={{
          width: '2.75rem', height: '2.75rem', borderRadius: '0.75rem', flexShrink: 0,
          background: C.tealLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Stethoscope size={20} color={C.teal} aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted }}>{t('accounting.yourShareNet')}</div>
          {loading ? (
            <SkeletonText width="7rem" height={22} style={{ marginTop: '0.25rem' }} />
          ) : (
            <div style={{ fontSize: '1.375rem', fontWeight: 800, color: C.teal, lineHeight: 1.2 }}>
              {share}
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.25rem' }}>{moneyUnit(currency)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer — the three supporting figures as full-width rows. */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
        marginTop: '0.875rem', paddingTop: '0.75rem', borderTop: `1px solid ${C.border}`,
      }}>
        <Row Icon={Wallet}       label={t('accounting.grandTotal')}  value={loading ? '—' : formatMoney(group.grossTotal, { currency })}            unit={short} color={C.navy} />
        <Row Icon={CheckCircle2} label={t('accounting.collected')}        value={loading ? '—' : formatMoney(group.collectedDoctorShare, { currency })} unit={short} color={C.green} />
        <Row Icon={Clock}        label={t('accounting.awaitingCollection')}  value={loading ? '—' : formatMoney(group.outstanding, { currency })}          unit={short} color={CT.warnInk} />
      </div>
    </div>
  );
};

const MobileAccountingSummary = ({ totals, loading, stale, testId = 'accounting-summary' }) => {
  const groups = totals || [];
  const mixed = groups.length > 1;

  return (
    <div
      data-testid={testId}
      data-state={loading ? 'loading' : 'content'}
      style={{
        background: 'white', borderRadius: '1rem',
        border: `1px solid ${C.border}`, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        padding: '1rem 1.125rem 0.875rem',
        transition: 'opacity 0.2s', opacity: stale ? 0.6 : 1,
        ...(mixed ? { display: 'flex', flexDirection: 'column', gap: '1rem' } : null),
      }}
    >
      {groups.map((group, i) => (
        <div
          key={group.currency}
          style={i > 0 ? { paddingTop: '1rem', borderTop: `1px solid ${C.border}` } : undefined}
        >
          <SummaryGroup group={group} loading={loading} heading={mixed} />
        </div>
      ))}
    </div>
  );
};

export default MobileAccountingSummary;
